import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.MouseEvent

/**
 * Two reusable building blocks for the Modify-category UX:
 *   - [mountHsvColorPicker]: a saturation/value square plus a vertical hue strip.
 *     Reports hex colour changes live via the supplied callback.
 *   - [mountIconGrid]: a 6×6 grid with 36 curated icons plus a "no icon"
 *     placeholder; returns the selected icon id via callback.
 *
 * Both mount their markup into the element passed in. They deliberately avoid
 * kotlinx.html so callers can drop them into an already-built container.
 */

/** Convert HSV (h in 0..360, s/v in 0..1) to a #RRGGBB hex string. */
fun hsvToHex(h: Double, s: Double, v: Double): String {
    val c = v * s
    val hh = (h / 60.0) % 6
    val x = c * (1 - kotlin.math.abs(hh % 2 - 1))
    val m = v - c
    val (r1, g1, b1) = when {
        hh < 1 -> Triple(c, x, 0.0)
        hh < 2 -> Triple(x, c, 0.0)
        hh < 3 -> Triple(0.0, c, x)
        hh < 4 -> Triple(0.0, x, c)
        hh < 5 -> Triple(x, 0.0, c)
        else   -> Triple(c, 0.0, x)
    }
    val r = ((r1 + m) * 255).toInt().coerceIn(0, 255)
    val g = ((g1 + m) * 255).toInt().coerceIn(0, 255)
    val b = ((b1 + m) * 255).toInt().coerceIn(0, 255)
    return "#" + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0')
}

/** Parse #RRGGBB into HSV (h: 0..360, s: 0..1, v: 0..1). Returns null for bad input. */
fun hexToHsv(hex: String): Triple<Double, Double, Double>? {
    val clean = hex.removePrefix("#").trim()
    if (clean.length != 6) return null
    val r = clean.substring(0, 2).toIntOrNull(16) ?: return null
    val g = clean.substring(2, 4).toIntOrNull(16) ?: return null
    val b = clean.substring(4, 6).toIntOrNull(16) ?: return null
    val rf = r / 255.0
    val gf = g / 255.0
    val bf = b / 255.0
    val max = maxOf(rf, gf, bf)
    val min = minOf(rf, gf, bf)
    val d = max - min
    val h = when {
        d == 0.0 -> 0.0
        max == rf -> 60.0 * (((gf - bf) / d) % 6)
        max == gf -> 60.0 * (((bf - rf) / d) + 2)
        else      -> 60.0 * (((rf - gf) / d) + 4)
    }.let { if (it < 0) it + 360.0 else it }
    val s = if (max == 0.0) 0.0 else d / max
    return Triple(h, s, max)
}

/**
 * Mounts an HSV colour picker (S/V square + hue strip) into [container].
 * Fires [onChange] with a new hex string whenever the user drags either widget.
 */
fun mountHsvColorPicker(
    container: HTMLElement,
    initialHex: String,
    size: Int = 220,
    onChange: (String) -> Unit,
) {
    container.innerHTML = ""

    val (h0, s0, v0) = hexToHsv(initialHex) ?: Triple(0.0, 1.0, 1.0)
    var hue = h0
    var sat = s0
    var value = v0

    val wrapper = document.createElement("div") as HTMLElement
    wrapper.setAttribute("style", "display: flex; gap: 14px; align-items: center;")

    val plane = document.createElement("div") as HTMLElement
    plane.setAttribute("style", """
        position: relative;
        width: ${size}px; height: ${size}px;
        border-radius: 10px; overflow: hidden;
        cursor: crosshair; touch-action: none;
    """.trimIndent())

    val hueStrip = document.createElement("div") as HTMLElement
    val stripW = 22
    hueStrip.setAttribute("style", """
        position: relative;
        width: ${stripW}px; height: ${size}px;
        border-radius: 10px;
        background: linear-gradient(to bottom,
            #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%,
            #0000ff 67%, #ff00ff 83%, #ff0000 100%);
        cursor: pointer; touch-action: none;
    """.trimIndent())

    // Medium preview circle to the right of the hue strip — shows the current pick.
    val previewWrap = document.createElement("div") as HTMLElement
    previewWrap.setAttribute("style", """
        flex: 1; display: flex; align-items: center; justify-content: center;
        min-width: 60px; align-self: stretch;
    """.trimIndent())
    val preview = document.createElement("div") as HTMLElement
    val previewSize = (size * 0.38).toInt().coerceAtLeast(56)
    preview.setAttribute("style", """
        width: ${previewSize}px; height: ${previewSize}px; border-radius: 50%;
        background: $initialHex;
        box-shadow: 0 2px 12px rgba(0,0,0,0.25) inset, 0 0 0 1px rgba(255,255,255,0.08);
    """.trimIndent())
    previewWrap.appendChild(preview)

    val planeCursor = document.createElement("div") as HTMLElement
    planeCursor.setAttribute("style", """
        position: absolute; width: 14px; height: 14px;
        border-radius: 50%; border: 2px solid #fff;
        box-shadow: 0 0 0 1px rgba(0,0,0,0.4);
        pointer-events: none; transform: translate(-50%, -50%);
    """.trimIndent())
    plane.appendChild(planeCursor)

    val hueCursor = document.createElement("div") as HTMLElement
    hueCursor.setAttribute("style", """
        position: absolute; left: -2px; right: -2px; height: 4px;
        background: #fff; border: 1px solid rgba(0,0,0,0.5);
        border-radius: 2px; pointer-events: none; transform: translateY(-50%);
    """.trimIndent())
    hueStrip.appendChild(hueCursor)

    fun refreshPlaneBg() {
        val pure = hsvToHex(hue, 1.0, 1.0)
        plane.style.background =
            "linear-gradient(to top, #000, transparent), " +
            "linear-gradient(to right, #fff, $pure)"
    }

    fun syncCursors() {
        planeCursor.style.left = "${(sat * size).toInt()}px"
        planeCursor.style.top = "${((1 - value) * size).toInt()}px"
        hueCursor.style.top = "${((hue / 360.0) * size).toInt()}px"
    }

    fun emit() {
        val hex = hsvToHex(hue, sat, value)
        preview.style.background = hex
        onChange(hex)
    }

    refreshPlaneBg()
    syncCursors()

    // Plane drag: update sat/value
    var planeDragging = false
    val onPlaneMove: (MouseEvent) -> Unit = { ev ->
        val rect = plane.getBoundingClientRect()
        val x = (ev.clientX - rect.left).coerceIn(0.0, rect.width)
        val y = (ev.clientY - rect.top).coerceIn(0.0, rect.height)
        sat = x / rect.width
        value = 1 - (y / rect.height)
        syncCursors()
        emit()
    }
    plane.addEventListener("pointerdown", { ev ->
        planeDragging = true
        (ev as? MouseEvent)?.let(onPlaneMove)
        (ev.currentTarget as? HTMLElement)?.setPointerCapture((ev.asDynamic().pointerId as Number).toInt())
    })
    plane.addEventListener("pointermove", { ev ->
        if (planeDragging) (ev as? MouseEvent)?.let(onPlaneMove)
    })
    plane.addEventListener("pointerup", { planeDragging = false })
    plane.addEventListener("pointercancel", { planeDragging = false })

    // Hue drag: update hue
    var hueDragging = false
    val onHueMove: (MouseEvent) -> Unit = { ev ->
        val rect = hueStrip.getBoundingClientRect()
        val y = (ev.clientY - rect.top).coerceIn(0.0, rect.height)
        hue = (y / rect.height) * 360.0
        refreshPlaneBg()
        syncCursors()
        emit()
    }
    hueStrip.addEventListener("pointerdown", { ev ->
        hueDragging = true
        (ev as? MouseEvent)?.let(onHueMove)
        (ev.currentTarget as? HTMLElement)?.setPointerCapture((ev.asDynamic().pointerId as Number).toInt())
    })
    hueStrip.addEventListener("pointermove", { ev ->
        if (hueDragging) (ev as? MouseEvent)?.let(onHueMove)
    })
    hueStrip.addEventListener("pointerup", { hueDragging = false })
    hueStrip.addEventListener("pointercancel", { hueDragging = false })

    wrapper.appendChild(plane)
    wrapper.appendChild(hueStrip)
    wrapper.appendChild(previewWrap)
    container.appendChild(wrapper)
}

/**
 * Mounts a 6×6 icon grid into [container]. [selectedId] must match one of
 * [utils.CustomCategoryIcons.ids]; picking is required, so there is no
 * "no-icon" option. Clicking a tile calls [onSelect] with the chosen id.
 */
fun mountIconGrid(
    container: HTMLElement,
    selectedId: String,
    strokeColor: String,
    onSelect: (String) -> Unit,
) {
    container.innerHTML = ""
    val grid = document.createElement("div") as HTMLElement
    grid.setAttribute("style", "display: grid; grid-template-columns: repeat(6, 40px); gap: 6px; justify-content: center;")

    utils.CustomCategoryIcons.ids.forEach { id ->
        val svg = utils.CustomCategoryIcons.renderSvg(id, size = 20) ?: return@forEach
        val b = document.createElement("div") as HTMLElement
        val isSel = id == selectedId
        b.setAttribute("style", """
            width: 40px; height: 40px; border-radius: 8px;
            display: flex; align-items: center; justify-content: center;
            background: rgba(128,128,128,0.08);
            border: 2px solid ${if (isSel) strokeColor else "transparent"};
            cursor: pointer; color: $strokeColor;
            transition: background 0.15s ease;
        """.trimIndent())
        b.innerHTML = svg
        b.addEventListener("mouseover", { b.style.background = "rgba(128,128,128,0.18)" })
        b.addEventListener("mouseout", { b.style.background = "rgba(128,128,128,0.08)" })
        b.addEventListener("click", { onSelect(id) })
        grid.appendChild(b)
    }
    container.appendChild(grid)
}
